// components/custom-ui/PdfViewerModal.tsx
// #47a — an in-app PDF preview built on the shared DocumentPreviewShell (same
// chrome as the Bibliotek SourceViewerModal). Takes a { url, title }.
//
// It fetches the bytes and renders a blob: URL rather than pointing the iframe at
// `url` directly — the document endpoint serves Content-Disposition: attachment,
// so a raw iframe src would DOWNLOAD the file (blank iframe + a stray browser
// download) instead of rendering it inline. A blob: URL renders inline.
//
// #356 — the download routes are authenticated now, so the bytes are fetched
// through the shared ApiClient rather than a bare fetch(). Two reasons, and the
// second is the one that bites:
//
//   1. its request interceptor attaches the bearer token, so this component
//      cannot forget to — it is the same path every other call in the app takes;
//   2. its response interceptor REFRESHES an expired token and retries. A bare
//      fetch() has no such recovery, so a tab left open past the access token's
//      hour would show "Kunde inte visa dokumentet" with nothing wrong but the
//      clock.
//
// It still renders a blob: URL rather than pointing the iframe at `url` — the
// endpoint serves Content-Disposition: attachment, so a raw iframe src would
// DOWNLOAD the file (blank iframe + a stray browser download) instead of
// rendering it inline. And `url` is no longer openable by the browser on its own:
// an iframe/anchor navigation sends no Authorization header and 401s. So
// "Öppna i ny flik" opens the blob we already hold, and is inert until it lands.
import React, { useEffect, useState } from 'react';
import { ExternalLink, FileText, Loader2, AlertCircle } from 'lucide-react';
import { DocumentPreviewShell } from '@/components/custom-ui/DocumentPreviewShell';
import { getApiClient } from '@/lib/api/client';

interface Props {
    url: string;
    title: string;
    onClose: () => void;
}

export const PdfViewerModal: React.FC<Props> = ({ url, title, onClose }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    // The HTTP status when the fetch failed — shown to the user. A bare
    // "couldn't display it" made a 401 and a missing file look identical, which
    // is exactly the ambiguity that made the last failure hard to read.
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        let created: string | null = null;
        setBlobUrl(null);
        setError(null);
        getApiClient()
            .get<Blob>(url, { responseType: 'blob' })
            .then((blob) => {
                if (cancelled) return;
                // Force the PDF content type so the browser renders (some responses
                // carry an octet-stream type that iframes won't display inline).
                const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
                created = URL.createObjectURL(pdfBlob);
                setBlobUrl(created);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const status = (err as { status?: number; statusCode?: number })?.status
                    ?? (err as { statusCode?: number })?.statusCode;
                setError(status ? `HTTP ${status}` : 'nätverksfel');
            });
        return () => {
            cancelled = true;
            if (created) URL.revokeObjectURL(created);
        };
    }, [url]);

    return (
        <DocumentPreviewShell
            onClose={onClose}
            icon={<FileText className="w-5 h-5 text-[#1A4D4F] flex-shrink-0" />}
            title={title}
            headerActions={
                blobUrl ? (
                    <a
                        href={blobUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1A4D4F] hover:bg-gray-100 rounded-lg"
                    >
                        <ExternalLink className="w-3.5 h-3.5" /> Öppna i ny flik
                    </a>
                ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 rounded-lg cursor-default">
                        <ExternalLink className="w-3.5 h-3.5" /> Öppna i ny flik
                    </span>
                )
            }
        >
            {error ? (
                <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                    <p className="text-sm text-gray-700">Kunde inte visa dokumentet.</p>
                    <p className="text-xs text-gray-400">({error})</p>
                </div>
            ) : blobUrl ? (
                <iframe src={blobUrl} title={title} className="w-full h-full" />
            ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laddar…
                </div>
            )}
        </DocumentPreviewShell>
    );
};

export default PdfViewerModal;
